import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import CustomDialog from '../CustomDialog'
import { useDispatch, useSelector } from 'react-redux'
import DropdownWithPlaceholder from '../DropdownWithPlaceholder'
import store, { RootState } from '../../store'
import { useNavigate } from 'react-router-dom'
import { ChangeEvent, useEffect, useState } from 'react'
import { createFile } from '../../services/filesService'
import { setShowUploadModal } from '../../actions/construction'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../models/AccessControl'
import { Can } from '../../utils/AbilityUtil'
import { createSelector } from '@reduxjs/toolkit'
import {
  showErrorMessage,
  updateSelectedClient,
  updateSelectedProject,
} from '../../actions/organization'
import DisplayErrorPopup from '../DisplayError'

const selectedOrganizationSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedOrganization,
)
const selectedClientSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedClient,
)
const selectedProjectSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedProject,
)
const clientsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.clients,
)
const projectsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.projects,
)

const AddFile = (props: any) => {
  const { isOpen, handleClose, disableClientDropdown, disableProjectDropdown } =
    props
  const [fileName, setFileName] = useState<string>()
  const [clientName, setClientName] = useState<string>()
  const [projectName, setProjectName] = useState<string>()
  const [isAddToNewClient, setIsAddToNewClient] = useState<boolean>(false)
  const [isAddToNewProject, setIsAddToNewProject] = useState<boolean>(false)
  const clients = useSelector(clientsSelector)
  const projects = useSelector(projectsSelector)
  const selectedOrganization = useSelector(selectedOrganizationSelector)
  const selectedClient = useSelector(selectedClientSelector)
  const selectedProject = useSelector(selectedProjectSelector)
  const navigate = useNavigate()
  const dispatch = useDispatch<any>()

  const handleSubmit = async () => {
    if (!isAddToNewProject && !!!selectedProject) {
      dispatch(
        showErrorMessage('Project not selected when creating a new file'),
      )
      return
    }

    if (!isAddToNewClient && !!!selectedClient) {
      dispatch(showErrorMessage('Client not selected when creating a new file'))
      return
    }

    if (isAddToNewProject) {
      const isProjectNameExists = projects.find(
        (project) => project.name === projectName,
      )
      if (isProjectNameExists) {
        dispatch(
          showErrorMessage('Project name should be unique within organization'),
        )
        return
      }
    }

    if (isAddToNewClient) {
      const isClientNameExists = clients.find(
        (client) => client.name === clientName,
      )
      if (isClientNameExists) {
        dispatch(
          showErrorMessage('Client name should be unique within organization'),
        )
        return
      }
    }

    await dispatch(
      createFile({
        filename: fileName!,
        projectId: isAddToNewProject ? undefined : selectedProject?.id,
        clientName: clientName,
        projectName: projectName,
        organizationId: selectedOrganization?.id,
        clientId: isAddToNewClient ? undefined : selectedClient?.id,
      }),
    ).unwrap()
    handleClose()

    const updatedProject = !isAddToNewProject
      ? selectedProject
      : selectedProjectSelector(store.getState())
    navigate(`/projects/${updatedProject?.id}/uploadFiles`)
    dispatch(setShowUploadModal(true))
  }

  const handleFileNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFileName(event.target.value)
  }

  const handleClientNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setClientName(event.target.value)
  }

  const handleProjectNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProjectName(event.target.value)
  }

  const handleClientSelect = (selectedItemId: string) => {
    const client = clients.find((client) => client.id === selectedItemId)
    dispatch(updateSelectedClient(client))
  }

  const handleProjectSelect = (selectedItemId: string) => {
    const project = projects.find((project) => project.id === selectedItemId)
    dispatch(updateSelectedProject(project))
  }

  const handleAddToNewClient = (value: boolean) => {
    setIsAddToNewClient(value)
    setIsAddToNewProject(value)
  }

  useEffect(() => {
    if (!isOpen) {
      setClientName('')
      setProjectName('')
      setFileName('')
    }
  }, [isOpen])

  return (
    <Can I={AttributeActionNames.Create} a={AttributeResourceNames.Files}>
      <CustomDialog
        isOpen={isOpen}
        title='Create a new file'
        handleClose={() => {
          setIsAddToNewClient(false)
          setIsAddToNewProject(false)
          handleClose()
        }}
        handleSubmit={async () => await handleSubmit()}
      >
        <DisplayErrorPopup />
        <Stack rowGap={'18px'} sx={{ mb: '12px' }}>
          <TextField
            sx={{
              width: '389px',
              '& .MuiInputBase-input': {
                color: '#04002E',
                fontSize: '14px',
                lineHeight: 'normal',
              },
              '& .MuiOutlinedInput-root': {
                boxShadow: '0px 3px 10px 0px rgba(187, 187, 187, 0.25)',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#C3C3C3',
                opacity: 1,
                fontSize: '14px',
              },
            }}
            size='small'
            placeholder='Add file address or name'
            autoFocus
            required
            margin='dense'
            id='name'
            name='name'
            value={fileName}
            onChange={handleFileNameChange}
          />

          {!disableClientDropdown ? (
            isAddToNewClient ? (
              <Stack rowGap={'15px'}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: 'normal',
                      color: '#989898',
                    }}
                  >
                    + Add new client
                  </Typography>
                </Box>
                <TextField
                  sx={{
                    width: '389px',
                    margin: 0,
                    '& .MuiInputBase-input': {
                      color: '#04002E',
                      fontSize: '14px',
                      lineHeight: 'normal',
                    },
                    '& .MuiOutlinedInput-root': {
                      boxShadow: '0px 3px 10px 0px rgba(187, 187, 187, 0.25)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#C3C3C3',
                      opacity: 1,
                      fontSize: '14px',
                    },
                  }}
                  size='small'
                  placeholder='Add client name'
                  autoFocus
                  required
                  margin='dense'
                  id='clientName'
                  name='clientName'
                  onChange={handleClientNameChange}
                />
              </Stack>
            ) : (
              <Stack rowGap={'15px'}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: 'normal',
                      color: '#989898',
                    }}
                  >
                    Add to existing client
                  </Typography>
                  <Button
                    disableRipple
                    variant='text'
                    onClick={() => handleAddToNewClient(true)}
                    sx={{
                      fontSize: '10px',
                      lineHeight: 'normal',
                      textTransform: 'none',
                      color: '#3F69FF',
                      padding: 0,
                    }}
                  >
                    + Add to new client
                  </Button>
                </Box>
                <DropdownWithPlaceholder
                  placeholder='Select client'
                  required={true}
                  listItems={clients?.map((client) => {
                    return { id: client.id, name: client.name }
                  })}
                  handleSelect={handleClientSelect}
                />
              </Stack>
            )
          ) : (
            ''
          )}

          {!disableProjectDropdown ? (
            isAddToNewClient || isAddToNewProject ? (
              <Stack rowGap={'15px'}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: 'normal',
                      color: '#989898',
                    }}
                  >
                    + Add new Project
                  </Typography>
                  {!isAddToNewClient ? (
                    <Button
                      variant='text'
                      onClick={() => setIsAddToNewProject(false)}
                      sx={{
                        fontSize: '10px',
                        lineHeight: 'normal',
                        textTransform: 'none',
                        color: '#3F69FF',
                        padding: 0,
                      }}
                    >
                      Add to existing Project
                    </Button>
                  ) : (
                    ''
                  )}
                </Box>
                <TextField
                  sx={{
                    width: '389px',
                    margin: 0,
                    '& .MuiInputBase-input': {
                      color: '#04002E',
                      fontSize: '14px',
                      lineHeight: 'normal',
                    },
                    '& .MuiOutlinedInput-root': {
                      boxShadow: '0px 3px 10px 0px rgba(187, 187, 187, 0.25)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#C3C3C3',
                      opacity: 1,
                      fontSize: '14px',
                    },
                  }}
                  size='small'
                  placeholder='Add Project name'
                  autoFocus
                  required
                  margin='dense'
                  id='projectName'
                  name='projectName'
                  onChange={handleProjectNameChange}
                />
              </Stack>
            ) : (
              <Stack rowGap={'15px'}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: 'normal',
                      color: '#989898',
                    }}
                  >
                    Add to existing project
                  </Typography>
                  <Button
                    disabled={!!!selectedClient?.id}
                    disableRipple
                    variant='text'
                    sx={{
                      fontSize: '10px',
                      lineHeight: 'normal',
                      textTransform: 'none',
                      color: '#3F69FF',
                      padding: 0,
                    }}
                    onClick={() => setIsAddToNewProject(true)}
                  >
                    + Add to new project
                  </Button>
                </Box>
                <DropdownWithPlaceholder
                  placeholder='Select project'
                  disabled={!!!selectedClient?.id}
                  required={true}
                  listItems={projects
                    .filter(
                      (project) => selectedClient?.id === project.client_id,
                    )
                    .map((project) => {
                      return { id: project.id, name: project.name }
                    })}
                  handleSelect={handleProjectSelect}
                />
              </Stack>
            )
          ) : (
            ''
          )}
        </Stack>
      </CustomDialog>
    </Can>
  )
}

export default AddFile
